import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import AddNew from 'assets/icons/add-new'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import CheckboxArrowIcon from 'assets/icons/checkbox-arrow-icon'
import { requestGetSitePressureSpecies } from 'api/monitoring'
import postRequest from 'utils/postRequest'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

import { BiodiversitySelectorWrapper, PaginationWrapper } from 'components/BiodiversitySelector/style'

import { ActionsRow, SpeciesList, SpeciesRow, ViewButton, ButtonContent, IconSlot, TextSlot } from './style'
import { SiteContext } from 'providers/site'
import { PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from 'flow/siteProposal/pages/about/style'
import { showErrorToast } from 'utils/toast'
import Loader from 'components/Loader'
import ViewIcon from 'assets/icons/view-site-icon'
import CircleIcon from 'assets/icons/circle-icon'

/* -------------------------
   Data + helpers (unchanged)
---------------------------- */
const typeOptions = [
  { label: 'Taxon Group & Species', value: 'all' },
  { label: 'Species', value: 'species' }
]

/* Recursively filter tree by kind, keeping parents when children match */
const filterTree = (nodes, filter) => {
  return nodes
    .map((node) => {
      const children = node.children ? filterTree(node.children, filter) : []
      const matches = filter === 'all' || node.kind === filter
      if (matches || (children && children.length)) {
        return { ...node, children }
      }
      return null
    })
    .filter(Boolean)
}

/* Build maps for quick lookup (id -> node, id -> parentId) */
const buildMaps = (nodes) => {
  const idToNode = new Map()
  const idToParent = new Map()

  const walk = (list, parent = null) => {
    list.forEach((node) => {
      idToNode.set(node.id, node)
      if (parent) idToParent.set(node.id, parent.id)
      if (node.children) walk(node.children, node)
    })
  }

  walk(nodes, null)
  return { idToNode, idToParent }
}

/* Get all descendant ids of a node (including the node itself) */
const collectDescendantIds = (node) => {
  const ids = []
  const walk = (n) => {
    ids.push(n.id)
    if (n.children) {
      n.children.forEach((c) => walk(c))
    }
  }
  walk(node)
  return ids
}

/* Get all ancestor ids for a node (parent, grandparent, ...), top-down order */
const collectAncestorIds = (id, idToParent) => {
  const ancestors = []
  let current = idToParent.get(id)
  while (current) {
    ancestors.push(current)
    current = idToParent.get(current)
  }
  return ancestors
}

const collectSpeciesDescendantIds = (node) => {
  const ids = []

  const walk = (currentNode) => {
    if (!currentNode) return
    if (currentNode.kind === 'species') {
      ids.push(currentNode.id)
    }
    if (currentNode.children && currentNode.children.length) {
      currentNode.children.forEach((child) => walk(child))
    }
  }

  walk(node)
  return ids
}

const buildGroupId = (groupName, fallback) => {
  const normalizedName = String(groupName ?? fallback ?? 'unknown-group')
    .trim()
    .toLowerCase()
  return `group-${encodeURIComponent(normalizedName || String(fallback ?? 'unknown-group'))}`
}

/* -------------------------
   Component
---------------------------- */
const TriggerSpeciesTab = ({
  onAddToEffect,
  threatItemId,
  onSelectionChange,
  setIncomingSelection,
  setMainTab,
  titleValue,
  modalTitleId,
  selectionResetKey,
  totalTriggers,
  onCancel,
  onBackToPressure
}) => {
  const site_threat_timing_id = localStorage.getItem('site_threat_timing_id')
  const params = useParams()
  const location = useLocation()
  const { site } = useContext(SiteContext)
  const [typeFilter, setTypeFilter] = useState(typeOptions[0])
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set())
  const [deselectedSpeciesIds, setDeselectedSpeciesIds] = useState(new Set())
  const [deselectedGroupIds, setDeselectedGroupIds] = useState(new Set())
  const selectedItemsRef = React.useRef(new Map())
  const selectedIdsRef = React.useRef(new Set())
  const selectedGroupIdsRef = React.useRef(new Set())
  const deselectedSpeciesIdsRef = React.useRef(new Set())
  const deselectedGroupIdsRef = React.useRef(new Set())
  const speciesGroupIdsRef = React.useRef(new Map())
  const globalSpeciesRawCache = React.useRef(new Map())
  const [pagination, setPagination] = useState({ currentPage: 1, resultsPerPage: 12 })
  const [taxonomicSpecies, setTaxonomicSpecies] = useState([])
  const [apiPaginationProvided, setApiPaginationProvided] = useState(false)
  const [loading, setLoading] = useState(false)
  const inFlightRef = React.useRef({ page: null })
  const didInitialFetchRef = React.useRef(false)
  const lastRequestedPaginationRef = React.useRef({ page: null, limit: null })

  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  useEffect(() => {
    selectedGroupIdsRef.current = selectedGroupIds
  }, [selectedGroupIds])

  useEffect(() => {
    deselectedSpeciesIdsRef.current = deselectedSpeciesIds
  }, [deselectedSpeciesIds])

  useEffect(() => {
    deselectedGroupIdsRef.current = deselectedGroupIds
  }, [deselectedGroupIds])

  const getSpeciesData = (page = pagination.currentPage, limit = pagination.resultsPerPage) => {
    // ignore additional requests while loading
    if (loading) return Promise.resolve()
    inFlightRef.current.page = page
    setLoading(true)
    const monitoringId = params?.monitoringId
    const monitorThreatId = threatItemId || threatItemId?.threatItemId || threatItemId?.triggerItem?.id
    requestGetSitePressureSpecies(params.id, monitoringId, monitorThreatId, page, limit)
      .then((data) => {
        const groups = Array.isArray(data?.data) ? data.data : []
        // transform to tree nodes expected by filterTree
        const nodes = groups
          // Sort groups alphabetically
          .sort((a, b) => (a.taxonomicGroup || '').localeCompare(b.taxonomicGroup || ''))
          .map((grp, gi) => ({
            id: buildGroupId(grp.taxonomicGroup, gi),
            name: grp.taxonomicGroup || `Group ${gi + 1}`,
            type: 'Taxon Group',
            kind: 'group',
            raw: (grp.group_threats_triggers && grp.group_threats_triggers[0]) || {},
            children: Array.isArray(grp.species)
              ? grp.species
                  // Sort species alphabetically
                  .sort((a, b) => (a.scientificName || '').localeCompare(b.scientificName || ''))
                  .map((s) => ({
                    id: s.historyId,
                    name: s.scientificName || `Species ${s.historyId}`,
                    commonName: s.specieName,
                    type: 'Species',
                    kind: 'species',
                    raw: s
                  }))
              : []
          }))

        setTaxonomicSpecies(nodes)

        try {
          nodes.forEach((grp) => {
            if (!Array.isArray(grp.children)) return
            grp.children.forEach((s) => {
              speciesGroupIdsRef.current.set(s.id, grp.id)
              globalSpeciesRawCache.current.set(s.id, { raw: s.raw, groupName: grp.name, groupRaw: grp.raw, groupId: grp.id })
            })
          })
        } catch (e) {}

        // persist raw data for any species on this page that are already selected
        try {
          nodes.forEach((grp) => {
            if (Array.isArray(grp.children)) {
              grp.children.forEach((s) => {
                if (selectedIdsRef.current.has(s.id)) {
                  selectedItemsRef.current.set(s.id, { raw: s.raw, groupName: grp.name, groupRaw: grp.raw, groupId: grp.id })
                }
              })
            }
          })
        } catch (e) {}

        // seed selectedIds from API-provided triggers/checked flags so items returned
        // as checked by the server appear selected on initial load
        try {
          const seeded = new Set()
          const activeSelectedIds = selectedIdsRef.current
          const activeSelectedGroupIds = selectedGroupIdsRef.current
          const blockedSpeciesIds = deselectedSpeciesIdsRef.current
          const blockedGroupIds = deselectedGroupIdsRef.current
          nodes.forEach((grp) => {
            const isWholeGroupSelected = activeSelectedGroupIds.has(grp.id) && !blockedGroupIds.has(grp.id)
            if (!grp || !Array.isArray(grp.children)) return
            grp.children.forEach((s) => {
              const raw = s.raw || {}
              const blockedByUser = blockedGroupIds.has(grp.id) || blockedSpeciesIds.has(s.id)
              const isSelectedLocally = !blockedByUser && activeSelectedIds.has(s.id)
              const hasTrigger = Boolean(
                raw.monitor_threats_triggers_id || raw.monitorThreatsTriggersId || raw.triggerId
              )
              const hasTriggerArray =
                (Array.isArray(raw.monitor_threats_trigger) && raw.monitor_threats_trigger.length > 0) ||
                (Array.isArray(raw.monitor_threats_triggers) && raw.monitor_threats_triggers.length > 0) ||
                (Array.isArray(raw.monitorThreatsTrigger) && raw.monitorThreatsTrigger.length > 0) ||
                (Array.isArray(raw.monitorThreatsTriggers) && raw.monitorThreatsTriggers.length > 0)
              const isCheckedFlag = raw.isChecked === true || raw.checked === true || raw.selected === true
              const shouldSeedFromServer = !blockedByUser && (hasTrigger || hasTriggerArray || isCheckedFlag)

              if (isSelectedLocally || isWholeGroupSelected || shouldSeedFromServer) {
                seeded.add(s.id)
                try {
                  // persist raw for cross-page payloads
                  selectedItemsRef.current.set(s.id, { raw: s.raw, groupName: grp.name, groupRaw: grp.raw, groupId: grp.id })
                } catch (e) {}
              }
            })
          })

          if (seeded.size > 0) {
            setSelectedIds((prev) => {
              const next = new Set(prev)
              seeded.forEach((id) => next.add(id))
              return next
            })
          }
        } catch (e) {}

        // expand all groups by default
        const initialExpanded = new Set()
        nodes.forEach((grp) => {
          if (grp && grp.id) initialExpanded.add(grp.id)
        })
        setExpandedIds(initialExpanded)

        // if API provided pagination, map pagination fields into local pagination state
        const pageInfo = data?.pagination
        if (pageInfo) {
          setApiPaginationProvided(true)
          setPagination((p) => ({
            ...p,
            resultsPerPage: pageInfo.resultsPerPage ?? p.resultsPerPage,
            currentPage: pageInfo.currentPage ?? p.currentPage,
            totalNumber: pageInfo.totalNumber ?? p.totalNumber,
            totalPages: pageInfo.totalPages ?? p.totalPages,
            hasNext: pageInfo.hasNext ?? p.hasNext,
            hasPrevious: pageInfo.hasPrevious ?? p.hasPrevious
          }))
        } else {
          setApiPaginationProvided(false)
        }
      })
      .catch((err) => {
        console.error('[TriggerSpeciesTab] requestGetSitePressureSpecies failed', err)
        setTaxonomicSpecies([])
      })
      .finally(() => {
        inFlightRef.current.page = null
        setLoading(false)
      })
  }

  // Clear selections when parent requests a reset (selectionResetKey increments)
  useEffect(() => {
    if (selectionResetKey === undefined) return
    try {
      setSelectedIds(new Set())
      setSelectedGroupIds(new Set())
      setDeselectedSpeciesIds(new Set())
      setDeselectedGroupIds(new Set())
      setExpandedIds(new Set())
      try {
        selectedItemsRef.current.clear()
      } catch (e) {}
    } catch (e) {}
  }, [selectionResetKey])

  // Fetch once on mount. After that, refetch only when server-side pagination is active
  // and page/page-size actually changed.
  useEffect(() => {
    const page = pagination.currentPage
    const limit = pagination.resultsPerPage
    const hasRequestChanged =
      lastRequestedPaginationRef.current.page !== page || lastRequestedPaginationRef.current.limit !== limit

    if (!didInitialFetchRef.current) {
      didInitialFetchRef.current = true
      lastRequestedPaginationRef.current = { page, limit }
      getSpeciesData(page, limit)
      return
    }

    if (!apiPaginationProvided || !hasRequestChanged) return

    lastRequestedPaginationRef.current = { page, limit }
    getSpeciesData(page, limit)
  }, [apiPaginationProvided, pagination.currentPage, pagination.resultsPerPage])

  /* Filtered tree (keeps hierarchy) - include taxonomicSpecies in deps so it updates when data loads */
  const filteredTree = useMemo(() => filterTree(taxonomicSpecies, typeFilter.value), [typeFilter, taxonomicSpecies])

  // Reset to page 1 only for local/client pagination when filter changes.
  useEffect(() => {
    if (apiPaginationProvided) return
    setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }))
  }, [apiPaginationProvided, typeFilter.value])

  /* Precompute maps for fast operations */
  const { idToNode, idToParent } = useMemo(() => buildMaps(filteredTree), [filteredTree])

  /* page-scoped ids (used for Select All) */
  /* If filter is 'species', present a flat list of species (no group parents) */
  const getSpeciesRoots = (nodes) => {
    const results = []
    const walk = (list) => {
      list.forEach((n) => {
        if (n.kind === 'species') results.push({ ...n, depth: 0 })
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(nodes)
    return results
  }

  const effectiveRoots = typeFilter && typeFilter.value === 'species' ? getSpeciesRoots(filteredTree) : filteredTree

  /* roots for pagination - use the effective roots so selection maps line up with rendered nodes */
  const roots = effectiveRoots
  // compute total species count across the filtered tree so pagination reflects species totals when in species mode
  const speciesCount = useMemo(() => {
    let count = 0
    const walk = (list) => {
      list.forEach((n) => {
        if (n.kind === 'species') count += 1
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(filteredTree)
    return count
  }, [filteredTree])

  const totalItems =
    typeFilter && typeFilter.value === 'species'
      ? apiPaginationProvided && typeof pagination.totalNumber === 'number'
        ? pagination.totalNumber
        : speciesCount
      : typeof pagination.totalNumber === 'number'
        ? pagination.totalNumber
        : roots.length

  const totalPages = pagination.totalPages ?? Math.max(1, Math.ceil(totalItems / pagination.resultsPerPage))
  // if API returned paginated items (apiPaginationProvided), taxonomicSpecies/filteredTree already contains current page items
  const pageItems = apiPaginationProvided
    ? roots
    : roots.slice(
        (pagination.currentPage - 1) * pagination.resultsPerPage,
        pagination.currentPage * pagination.resultsPerPage
      )
  const pageSpeciesIds = useMemo(() => {
    const ids = []
    const walk = (nodes) => {
      nodes.forEach((n) => {
        if (n.kind === 'species') ids.push(n.id)
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(pageItems)
    return ids
  }, [pageItems])

  const pageGroupIds = useMemo(() => {
    const ids = []
    const walk = (nodes) => {
      nodes.forEach((n) => {
        if (n.kind === 'group') ids.push(n.id)
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(pageItems)
    return ids
  }, [pageItems])
  /* Expand/collapse toggler */
  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const isAllSelected = pageSpeciesIds.length > 0 && pageSpeciesIds.every((id) => selectedIds.has(id))

  const isGroupChecked = useCallback(
    (node, selectedSet = selectedIds, selectedGroupSet = selectedGroupIds) => {
      const speciesDescendantIds = collectSpeciesDescendantIds(node)
      const isMissingLocally = speciesDescendantIds.some(id => !selectedSet.has(id))

      if (selectedGroupSet.has(node.id) && !isMissingLocally) return true

      return speciesDescendantIds.length > 0 && speciesDescendantIds.every((id) => selectedSet.has(id))
    },
    [selectedGroupIds, selectedIds]
  )

  // count only species selections (exclude group ids)
  const selectedSpeciesCount = useMemo(() => {
    try {
      // Build a set of species ids from persisted selections across pages
      const speciesIds = new Set()
      try {
        selectedItemsRef.current.forEach((v, k) => {
          speciesIds.add(k)
        })
      } catch (e) {}

      // Also include any species ids that are in the current idToNode map and selectedIds
      selectedIds.forEach((id) => {
        const node = idToNode ? idToNode.get(id) : null
        if (node && node.kind === 'species') speciesIds.add(id)
      })

      return speciesIds.size
    } catch (e) {
      return 0
    }
  }, [selectedIds, idToNode])

  // report selection count to parent when it changes
  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange({ species: selectedSpeciesCount, ecosystems: 0 })
    }
  }, [selectedSpeciesCount, onSelectionChange])

  const handleSelectAll = useCallback(() => {
    if (pageSpeciesIds.length === 0) return
    setSelectedIds((prev) => {
      const next = new Set(prev)

      if (isAllSelected) {
        pageSpeciesIds.forEach((id) => {
          next.delete(id)
          try {
            selectedItemsRef.current.delete(id)
          } catch (e) {}
        })

        if (pageGroupIds.length > 0) {
          try {
            speciesGroupIdsRef.current.forEach((mappedGroupId, speciesId) => {
              if (pageGroupIds.includes(mappedGroupId)) {
                next.delete(speciesId)
                selectedItemsRef.current.delete(speciesId)
              }
            })
          } catch(e) {}
        }
        return next
      }

      pageSpeciesIds.forEach((id) => {
        next.add(id)
        try {
          const n = idToNode.get(id)
          if (n && n.kind === 'species' && n.raw) {
            let parentId = idToParent.get(id)
            let groupName = ''
            let groupRaw = {}
            while (parentId) {
              const p = idToNode.get(parentId)
              if (p && p.kind === 'group') {
                groupName = p.name
                groupRaw = p.raw || {}
                break
              }
              parentId = idToParent.get(parentId)
            }
            selectedItemsRef.current.set(id, { raw: n.raw, groupName, groupRaw, groupId: groupName ? buildGroupId(groupName, parentId) : null })
          }
        } catch (e) {}
      })

      if (pageGroupIds.length > 0) {
        try {
          speciesGroupIdsRef.current.forEach((mappedGroupId, speciesId) => {
            if (pageGroupIds.includes(mappedGroupId)) {
              next.add(speciesId)
              const cached = globalSpeciesRawCache.current.get(speciesId)
              if (cached) selectedItemsRef.current.set(speciesId, cached)
            }
          })
        } catch(e) {}
      }

      return next
    })
    setDeselectedSpeciesIds((prev) => {
      const next = new Set(prev)
      pageSpeciesIds.forEach((speciesId) => {
        if (isAllSelected) next.add(speciesId)
        else next.delete(speciesId)
      })

      if (pageGroupIds.length > 0) {
        try {
          speciesGroupIdsRef.current.forEach((mappedGroupId, speciesId) => {
            if (pageGroupIds.includes(mappedGroupId)) {
              if (isAllSelected) next.add(speciesId)
              else next.delete(speciesId)
            }
          })
        } catch(e) {}
      }
      return next
    })

    if (isAllSelected) {
      setSelectedGroupIds((prev) => {
        const next = new Set(prev)
        pageGroupIds.forEach((groupId) => next.delete(groupId))
        return next
      })
      setDeselectedGroupIds((prev) => {
        const next = new Set(prev)
        pageGroupIds.forEach((groupId) => next.add(groupId))
        return next
      })
    } else {
      setSelectedGroupIds((prev) => {
        const next = new Set(prev)
        pageGroupIds.forEach((groupId) => next.add(groupId))
        return next
      })
      setDeselectedGroupIds((prev) => {
        const next = new Set(prev)
        pageGroupIds.forEach((groupId) => next.delete(groupId))
        return next
      })
    }
  }, [isAllSelected, pageGroupIds, pageSpeciesIds, idToNode, idToParent])

  const navigate = useNavigate()

  const handleAddToEvaluation = useCallback(async () => {
    const localThreatTimmingId = localStorage.getItem('site_threat_timing_id')
    const extractThreatId = (val) => {
      if (val == null) return null
      if (typeof val === 'string' || typeof val === 'number') return val
      if (typeof val === 'object') {
        return val.threatItemId || val.id || (val.triggerItem && val.triggerItem.id) || null
      }
      return null
    }

    const monitorThreatId = extractThreatId(threatItemId)

    // Group structures tracking maps
    const speciesIdsByGroupId = new Map()
    const deselectedSpeciesIdsByGroupId = new Map()
    const groupNameByGroupId = new Map()

    try {
      selectedItemsRef.current.forEach((val, speciesId) => {
        if (val.groupId) {
          groupNameByGroupId.set(val.groupId, val.groupName)
          if (!speciesIdsByGroupId.has(val.groupId)) speciesIdsByGroupId.set(val.groupId, [])
          speciesIdsByGroupId.get(val.groupId).push(val.raw?.historyId || val.raw?.siteSpeciesId || val.raw?.site_species_id || speciesId)
        }
      })
    } catch (e) {}

    try {
      deselectedSpeciesIds.forEach((speciesId) => {
        const groupId = speciesGroupIdsRef.current.get(speciesId)
        if (groupId) {
          if (!deselectedSpeciesIdsByGroupId.has(groupId)) deselectedSpeciesIdsByGroupId.set(groupId, [])
          deselectedSpeciesIdsByGroupId.get(groupId).push(speciesId)
        }
      })
    } catch (e) {}

    const inferGroupName = (groupId) => {
      if (groupNameByGroupId.has(groupId)) return groupNameByGroupId.get(groupId)
      if (groupId && groupId.startsWith('group-')) {
        return decodeURIComponent(groupId.replace('group-', ''))
      }
      return groupId
    }

    const taxonGroupsPayload = []
    const processedGroupIds = new Set()

    // Case 1 & Case 2: Process originally fully selected groups
    selectedGroupIdsRef.current.forEach((groupId) => {
      processedGroupIds.add(groupId)
      const groupName = inferGroupName(groupId)
      const deselected = deselectedSpeciesIdsByGroupId.get(groupId) || []

      if (deselected.length > 0) {
        // Case 2
        taxonGroupsPayload.push({
          [groupName]: {
            removed_species: deselected,
            isAllSpecies: true
          }
        })
      } else {
        // Case 1
        taxonGroupsPayload.push({
          [groupName]: {
            isAllSpecies: true
          }
        })
      }
    })

    // Case 3: Process remaining groups that have species selected but NOT in selectedGroupIds
    speciesIdsByGroupId.forEach((speciesArray, groupId) => {
      if (!processedGroupIds.has(groupId)) {
        processedGroupIds.add(groupId)
        const groupName = inferGroupName(groupId)
        const validSpeciesArray = speciesArray.filter(Boolean)
        if (validSpeciesArray.length > 0) {
          taxonGroupsPayload.push({
            [groupName]: {
              species: validSpeciesArray,
              isAllSpecies: false
            }
          })
        }
      }
    })

    const finalApiPayload = {
      monitor_threats_id: Number(monitorThreatId),
      site_threat_timing_id: Number(site_threat_timing_id) || Number(localThreatTimmingId) || null,
      site_pressure_id: Number(params.pressureId) || null,
      class_names: taxonGroupsPayload
    }

    if (taxonGroupsPayload.length === 0) {
      showErrorToast(<FormattedMessage id="triggerSpeciesNoSelectMessage" />)
      return
    }

    try {
      await postRequest(`/api/monitor-threats-triggers/taxon-groups`, finalApiPayload)
    } catch (err) {
      console.error('Failed API call', err)
    }

    const groupsMap = new Map()
    try {
      selectedItemsRef.current.forEach((val, id) => {
        const groupName = val.groupName || 'Ungrouped'
        const groupRaw = val.groupRaw || {}
        if (!groupsMap.has(groupName)) groupsMap.set(groupName, { species: [], groupRaw })
        groupsMap.get(groupName).species.push(val.raw)
      })
    } catch (e) {}

    const groups = []
    groupsMap.forEach((val, taxonomicGroup) => {
      groups.push({ taxonomicGroup, raw: val.groupRaw, species: val.species })
    })

    const payload = {
      screencheck: true,
      data: groups,
      threatItemId: monitorThreatId,
      displayName: threatItemId?.titleValue || modalTitleId || titleValue || '',
      pagination: {
        totalNumber: groups.reduce((acc, g) => acc + (g.species?.length || 0), 0),
        resultsPerPage: pagination.resultsPerPage,
        totalPages: 1,
        currentPage: pagination.currentPage,
        hasNext: false,
        hasPrevious: false
      }
    }

    const siteId = params?.id || site?.id
    const monitoringId = params?.monitoringId

    if (siteId && monitoringId) {
      try {
        if (typeof onAddToEffect === 'function') onAddToEffect(payload)
      } catch (e) {}

      const safePayload = JSON.parse(JSON.stringify(payload))
      const returnToPath = location?.pathname || '/'
      const modalThreatId = extractThreatId(threatItemId)
      navigate(`/site/${siteId}/monitoring/${monitoringId}/pressure`, {
        state: { preselected: safePayload, openAddDetails: true, modalOpen: true, modalThreatId, returnToPath }
      })
      return
    }

    if (typeof onAddToEffect === 'function') {
      onAddToEffect(payload)
      return
    }
  }, [apiPaginationProvided, onAddToEffect, navigate, pagination, params, site, location, threatItemId, totalPages, deselectedSpeciesIds])

  /* helper: select node + descendants */
  const selectNodeWithDescendants = useCallback(
    (nodeId, selectedSet) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const desc = collectDescendantIds(node)
      // persist raw data for species that are present in the current page
      desc.forEach((id) => {
        try {
          const n = idToNode.get(id)
          if (n && n.kind === 'species' && n.raw) {
            selectedSet.add(id)
            // find group name (closest ancestor of kind 'group')
            let parentId = idToParent.get(id)
            let groupName = ''
            let groupRaw = {}
            while (parentId) {
              const p = idToNode.get(parentId)
              if (p && p.kind === 'group') {
                groupName = p.name
                groupRaw = p.raw || {}
                break
              }
              parentId = idToParent.get(parentId)
            }
            selectedItemsRef.current.set(id, {
              raw: n.raw,
              groupName,
              groupRaw,
              groupId: groupName ? buildGroupId(groupName, parentId) : nodeId
            })
          }
        } catch (e) {}
      })
    },
    [idToNode, idToParent]
  )

  const deselectGroupSpecies = useCallback(
    (groupNode, selectedSet, deselectedSpeciesSet) => {
      if (!groupNode) return

      const speciesIdsToRemove = new Set(collectSpeciesDescendantIds(groupNode))

      try {
        speciesGroupIdsRef.current.forEach((mappedGroupId, speciesId) => {
          if (mappedGroupId === groupNode.id) {
            speciesIdsToRemove.add(speciesId)
          }
        })
      } catch (e) {}

      try {
        selectedItemsRef.current.forEach((value, speciesId) => {
          if (value?.groupId === groupNode.id) {
            speciesIdsToRemove.add(speciesId)
          }
        })
      } catch (e) {}

      speciesIdsToRemove.forEach((speciesId) => {
        selectedSet.delete(speciesId)
        deselectedSpeciesSet.add(speciesId)
        try {
          selectedItemsRef.current.delete(speciesId)
        } catch (e) {}
      })
    },
    []
  )

  const handleToggleNode = useCallback(
    (node) => {
      // node is an object from the tree
      const nodeId = node.id
      const nextSelected = new Set(selectedIds)
      const nextSelectedGroupIds = new Set(selectedGroupIds)
      const nextDeselectedSpeciesIds = new Set(deselectedSpeciesIds)
      const nextDeselectedGroupIds = new Set(deselectedGroupIds)

      const isLeaf = node.kind === 'species' || !node.children || node.children.length === 0
      const currentlySelected = isLeaf ? selectedIds.has(nodeId) : isGroupChecked(node, selectedIds, selectedGroupIds)

      if (isLeaf) {
        // If it's a child/species, toggle the child and update ancestor group checks
        if (!currentlySelected) {
          nextSelected.add(nodeId)
          nextDeselectedSpeciesIds.delete(nodeId)
          // persist this species raw for cross-page selection
          try {
            // find group name from ancestors
            let parentId = idToParent.get(nodeId)
            let groupName = ''
            let groupRaw = {}
            while (parentId) {
              const p = idToNode.get(parentId)
              if (p && p.kind === 'group') {
                groupName = p.name
                groupRaw = p.raw || {}
                break
              }
              parentId = idToParent.get(parentId)
            }
            if (node && node.raw) {
              selectedItemsRef.current.set(nodeId, {
                raw: node.raw,
                groupName,
                groupRaw,
                groupId: groupName ? buildGroupId(groupName, parentId) : null
              })
            }
          } catch (e) {}
        } else {
          nextSelected.delete(nodeId)
          nextDeselectedSpeciesIds.add(nodeId)
          try {
            selectedItemsRef.current.delete(nodeId)
          } catch (e) {}
        }
      } else {
        // It's a group: selecting selects all descendants; deselecting removes all descendants
        if (!currentlySelected) {
          nextSelectedGroupIds.add(nodeId)
          nextDeselectedGroupIds.delete(nodeId)
          selectNodeWithDescendants(nodeId, nextSelected)

          const speciesToRestore = new Set(collectSpeciesDescendantIds(node))
          try {
            speciesGroupIdsRef.current.forEach((mappedGroupId, speciesId) => {
              if (mappedGroupId === nodeId) speciesToRestore.add(speciesId)
            })
          } catch(e) {}

          speciesToRestore.forEach((speciesId) => {
            nextDeselectedSpeciesIds.delete(speciesId)
            try {
              const cached = globalSpeciesRawCache.current.get(speciesId)
              if (cached) selectedItemsRef.current.set(speciesId, cached)
            } catch(e) {}
          })
        } else {
          nextSelectedGroupIds.delete(nodeId)
          nextDeselectedGroupIds.add(nodeId)
          deselectGroupSpecies(node, nextSelected, nextDeselectedSpeciesIds)
        }
      }

      setSelectedIds(nextSelected)
      setSelectedGroupIds(nextSelectedGroupIds)
      setDeselectedSpeciesIds(nextDeselectedSpeciesIds)
      setDeselectedGroupIds(nextDeselectedGroupIds)
    },
    [
      selectedIds,
      selectedGroupIds,
      deselectedSpeciesIds,
      deselectedGroupIds,
      idToNode,
      idToParent,
      isGroupChecked,
      selectNodeWithDescendants,
      deselectGroupSpecies
    ]
  )

  /* -------------------------
     Render helpers (recursive)
  ----------------------------*/
  const renderTreeRow = (node, depth = 0, isChild = false) => {
    const isExpanded = expandedIds.has(node.id)
    const hasChildren = Boolean(node.children && node.children.length)
    const checked = hasChildren ? isGroupChecked(node) : selectedIds.has(node.id)

    return (
      <React.Fragment key={node.id}>
        <SpeciesRow
          depth={depth}
          data-test={isChild}
          style={{
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 44,
            backgroundColor: isChild && '#f1f1f1'
          }}
        >
          <div className="left" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, whiteSpace: 'nowrap' }}>
            {isChild && (
              <span style={{ paddingLeft: '3px' }}>
                <CheckboxArrowIcon />
              </span>
            )}
            <Checkbox
              checked={checked}
              onChange={() => handleToggleNode(node)}
              label={
                <span className="species-label">
                  {/* <span className="status-dot blue" /> */}
                  <CircleIcon width="18" height="18" />
                  <em>{node.name}</em> {node.commonName}
                </span>
              }
            />
          </div>
        </SpeciesRow>

        {/* render children only when expanded */}
        {hasChildren && isExpanded && <div>{node.children.map((child) => renderTreeRow(child, depth + 1, true))}</div>}
      </React.Fragment>
    )
  }

  /* -------------------------
     Render
  ---------------------------- */
  return (
    <Container>
      <Row>
        <Col md={12}>
          <BiodiversitySelectorWrapper>
                <PageSubtitleWrapper style={{ marginTop: 20, justifyContent: 'flex-start', marginBottom: 0 }}>
                  <PageSubtitle style={{ borderBottom: 'none', fontSize: '14px', fontWeight: 'normal' }}>
                    <FormattedMessage id="selectTriggerSpeciesSubtitle" />
                  </PageSubtitle>
                  <Tooltip
                    placement="bottom"
                    trigger={['hover']}
                    align={{ offset: [0, 16] }}
                    zIndex={9999}
                    overlay={{
                      label: 'selectTriggerSpeciesSubtitle',
                      infoTooltip: 'selectTriggerSpeciesInfo'
                    }}
                    type="big"
                  >
                    <TooltipHintWrapper>
                      <InfoIcon />
                    </TooltipHintWrapper>
                  </Tooltip>
                </PageSubtitleWrapper>

            <ActionsRow style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '6px' }}>
                {totalItems > 0 && (
                  <Checkbox
                    label={<FormattedMessage id="selectAll" defaultMessage="selectAll" />}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {totalItems > 0 && (
                  <div>
                    <Button
                      type="primary"
                      withIcon
                      height="40px"
                      marginTop="0"
                      marginRight="150px"
                      disabled={selectedIds.size === 0}
                      isDisabled={totalItems === 0}
                      onClick={handleAddToEvaluation}
                      style={{ justifyContent: 'flex-end' }}
                    >
                      <AddNew />
                      <FormattedMessage id="addTriggersToEvaluationBtn" defaultMessage="addTriggersToEvaluationBtn" />
                    </Button>
                  </div>
                )}

                {!totalTriggers && (
                  <div>
                    <ViewButton
                      onClick={() => {
                        onCancel?.(false)
                      }}
                    >
                      <ButtonContent>
                        <IconSlot>
                          <ViewIcon />
                        </IconSlot>
                        <TextSlot>
                          <FormattedMessage id="viewStateEvaluation" />
                        </TextSlot>
                      </ButtonContent>
                    </ViewButton>
                  </div>
                )}
              </div>
            </ActionsRow>

            <SpeciesList>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Loader />
                </div>
              ) : totalItems === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
                  <FormattedMessage id="noRecordsFound" defaultMessage="noRecordsFound" />
                </div>
              ) : (
                <>{pageItems.map((node) => renderTreeRow(node))}</>
              )}
            </SpeciesList>
            {totalItems > 0 && (
              <PaginationWrapper>
                <ItemsPerPageDropdown
                  resultsPerPage={pagination.resultsPerPage}
                  totalNumber={totalItems}
                  currentPage={pagination.currentPage}
                  onChange={(option) => {
                    setPagination((prev) => ({ ...prev, resultsPerPage: option.value, currentPage: 1 }))
                  }}
                  menuPlacement={'top'}
                />
                {totalPages > 1 && (
                  <Pagination
                    activePages={pagination.currentPage}
                    numberOfPages={totalPages}
                    nextPage={() => {
                      if (loading) return
                      setPagination((prev) => ({ ...prev, currentPage: Math.min(totalPages, prev.currentPage + 1) }))
                    }}
                    prevPage={() => {
                      if (loading) return
                      setPagination((prev) => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))
                    }}
                    selectPage={(page) => {
                      if (loading || page === pagination.currentPage) return
                      setPagination((prev) => ({ ...prev, currentPage: page }))
                    }}
                  />
                )}
              </PaginationWrapper>
            )}
          </BiodiversitySelectorWrapper>
        </Col>
      </Row>
    </Container>
  )
}

export default TriggerSpeciesTab
